import { createPopper } from "@popperjs/core";
import { onMounted, onUnmounted, onUpdated, ref, unref, watch } from "vue";
function on(element, event, handler) {
    if (element && event && handler) {
        element.addEventListener(event, handler, false);
    }
}
function off(element, event, handler) {
    if (element && event) {
        element.removeEventListener(event, handler, false);
    }
}
const defaultTrigger = "hover";
export function usePopperjs(reference, popper, options) {
    const isMounted = ref(false);
    onMounted(() => {
        isMounted.value = true;
    });
    onUnmounted(() => {
        isMounted.value = false;
        destroy();
    });
    const updatedFlag = ref(true);
    onUpdated(() => {
        updatedFlag.value = !updatedFlag.value;
    });
    const referenceRef = ref();
    const popperRef = ref();
    watch(() => [isMounted.value, updatedFlag.value], () => {
        var _a, _b;
        if (!isMounted.value) {
            return;
        }
        if ((_a = unref(reference)) === null || _a === void 0 ? void 0 : _a.$el) {
            referenceRef.value = unref(reference).$el;
        }
        else {
            referenceRef.value = unref(reference);
        }
        if ((_b = unref(popper)) === null || _b === void 0 ? void 0 : _b.$el) {
            popperRef.value = unref(popper).$el;
        }
        else {
            popperRef.value = unref(popper);
        }
    });
    const instance = ref();
    watch(() => [referenceRef.value, popperRef.value], () => {
        destroy();
        if (!referenceRef.value) {
            return;
        }
        if (!popperRef.value) {
            return;
        }
        concrete();
    });
    const concrete = () => {
        var _a, _b, _c, _d;
        instance.value = createPopper(referenceRef.value, popperRef.value, {
            placement: (_a = options === null || options === void 0 ? void 0 : options.placement) !== null && _a !== void 0 ? _a : "bottom",
            modifiers: (_b = options === null || options === void 0 ? void 0 : options.modifiers) !== null && _b !== void 0 ? _b : [],
            strategy: (_c = options === null || options === void 0 ? void 0 : options.strategy) !== null && _c !== void 0 ? _c : "absolute",
            onFirstUpdate: (_d = options === null || options === void 0 ? void 0 : options.onFirstUpdate) !== null && _d !== void 0 ? _d : undefined,
        });
    };
    const destroy = () => {
        var _a;
        (_a = instance.value) === null || _a === void 0 ? void 0 : _a.destroy();
        instance.value = undefined;
    };
    const visible = ref(false);
    const doToggle = () => (visible.value = !visible.value);
    const doOpen = () => (visible.value = true);
    const doClose = () => (visible.value = false);
    watch(() => [instance.value, unref(options === null || options === void 0 ? void 0 : options.trigger), unref(options === null || options === void 0 ? void 0 : options.forceShow)], () => {
        if (!instance.value) {
            return;
        }
        if (unref(options === null || options === void 0 ? void 0 : options.forceShow)) {
            visible.value = true;
            doOff();
            return;
        }
        doOn();
    });
    watch(() => unref(options === null || options === void 0 ? void 0 : options.forceShow), () => {
        if (unref(options === null || options === void 0 ? void 0 : options.forceShow)) {
            return;
        }
        if (unref(options === null || options === void 0 ? void 0 : options.trigger) === "manual") {
            return;
        }
        visible.value = false;
    });
    watch(() => unref(options === null || options === void 0 ? void 0 : options.disabled), () => {
        if (unref(options === null || options === void 0 ? void 0 : options.disabled)) {
            doOff();
        }
        else {
            doOn();
        }
    });
    const timer = ref();
    const doMouseover = () => {
        var _a;
        if (unref(options === null || options === void 0 ? void 0 : options.delayOnMouseover) === 0) {
            doOpen();
        }
        else {
            clearTimeout(timer.value);
            timer.value = setTimeout(() => {
                doOpen();
            }, (_a = unref(options === null || options === void 0 ? void 0 : options.delayOnMouseover)) !== null && _a !== void 0 ? _a : 100);
        }
    };
    const doMouseout = () => {
        var _a;
        if (unref(options === null || options === void 0 ? void 0 : options.delayOnMouseout) === 0) {
            doClose();
        }
        else {
            clearTimeout(timer.value);
            timer.value = setTimeout(() => {
                doClose();
            }, (_a = unref(options === null || options === void 0 ? void 0 : options.delayOnMouseout)) !== null && _a !== void 0 ? _a : 100);
        }
    };
    const doOn = () => {
        var _a;
        doOff();
        switch ((_a = unref(options === null || options === void 0 ? void 0 : options.trigger)) !== null && _a !== void 0 ? _a : defaultTrigger) {
            case "click-to-open": {
                on(referenceRef.value, "click", doOpen);
                on(document, "click", doCloseForDocument);
                break;
            }
            case "click-to-toggle": {
                on(referenceRef.value, "click", doToggle);
                on(document, "click", doCloseForDocument);
                break;
            }
            case "hover": {
                on(referenceRef.value, "mouseover", doMouseover);
                on(popperRef.value, "mouseover", doMouseover);
                on(referenceRef.value, "mouseout", doMouseout);
                on(popperRef.value, "mouseout", doMouseout);
                on(referenceRef.value, "mousedown", doMouseout);
                on(popperRef.value, "mousedown", doMouseout);
                break;
            }
            case "focus": {
                on(referenceRef.value, "focus", doOpen);
                on(popperRef.value, "focus", doOpen);
                on(referenceRef.value, "blur", doClose);
                on(popperRef.value, "blur", doClose);
                break;
            }
            case "manual": {
                break;
            }
            default: {
                throw TypeError();
            }
        }
    };
    const doOff = () => {
        off(referenceRef.value, "click", doOpen);
        off(document, "click", doCloseForDocument);
        off(referenceRef.value, "click", doToggle);
        off(referenceRef.value, "mouseover", doMouseover);
        off(popperRef.value, "mouseover", doMouseover);
        off(referenceRef.value, "mouseout", doMouseout);
        off(popperRef.value, "mouseout", doMouseout);
        off(referenceRef.value, "mousedown", doMouseout);
        off(popperRef.value, "mousedown", doMouseout);
        off(referenceRef.value, "focus", doOpen);
        off(popperRef.value, "focus", doOpen);
        off(referenceRef.value, "blur", doClose);
        off(popperRef.value, "blur", doClose);
    };
    const doCloseForDocument = (e) => {
        var _a, _b;
        if ((_a = referenceRef.value) === null || _a === void 0 ? void 0 : _a.contains(e.target)) {
            return;
        }
        if ((_b = popperRef.value) === null || _b === void 0 ? void 0 : _b.contains(e.target)) {
            return;
        }
        doClose();
    };
    watch(() => [instance.value, visible.value], () => {
        var _a, _b, _c, _d, _e;
        if (!instance.value) {
            return;
        }
        if (visible.value || unref(options === null || options === void 0 ? void 0 : options.forceShow)) {
            (_a = popperRef.value) === null || _a === void 0 ? void 0 : _a.classList.remove("vue-use-popperjs-none");
            (_b = options === null || options === void 0 ? void 0 : options.onShow) === null || _b === void 0 ? void 0 : _b.call(options);
            (_c = instance.value) === null || _c === void 0 ? void 0 : _c.update();
        }
        else {
            (_d = popperRef.value) === null || _d === void 0 ? void 0 : _d.classList.add("vue-use-popperjs-none");
            (_e = options === null || options === void 0 ? void 0 : options.onHide) === null || _e === void 0 ? void 0 : _e.call(options);
        }
    });
    return {
        instance,
        visible,
    };
}
